import React, { forwardRef, useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react'

import classNames from 'classnames'

import { Dropdown, DropdownButton, DropdownContent, DropdownItem } from 'ui/components/Dropdown'
import { extractStandardProps } from 'ui/helpers/styles'
import { stopPropagation } from 'ui/helpers/utilities'

import * as Parts from './Tabs.parts'
import { useTabsContext } from './tabsContext'

import {
    ActionsContainerButtonStyle,
    ActionsDividerStyle,
    TAB_CHAR_WIDTH,
    TAB_CONTENT_GAP,
    TAB_ICON_WIDTH,
    TAB_MAX_WIDTH_ICON,
    TAB_MAX_WIDTH_NO_ICON,
    TAB_MIN_WIDTH_ICON,
    TAB_MIN_WIDTH_NO_ICON,
    TabsListCard,
    TabsListUnderlined,
} from './Tabs.css'

type TabsListRef = HTMLDivElement

type TabsListProps = Omit<
    React.ComponentPropsWithoutRef<typeof Parts.List>,
    'asChild' | 'overflow' | 'type' | 'hasAction'
> & {
    overflow?: boolean
    overflowMaxCount?: number
    autoOverflowMaxCount?: boolean
    overflowDropdownHead?: React.ComponentPropsWithoutRef<typeof DropdownContent>['head']
    endAction?: React.ReactNode
    renderOverflowDropdownItem?: ExtraTabsDropdownProps['renderDropdownItem']
}

export const TabsList = forwardRef<TabsListRef, TabsListProps>(
    (
        {
            children,
            overflow,
            overflowMaxCount: providedOverflowMaxCount = 5,
            autoOverflowMaxCount,
            endAction,
            renderOverflowDropdownItem,
            overflowDropdownHead,
            ...props
        },
        ref
    ) => {
        const allTabs = useMemo(() => {
            return React.Children.toArray(children).filter((child) =>
                React.isValidElement(child)
            ) as React.ReactElement[]
        }, [children])

        const listWrapperRef = useRef<HTMLDivElement>(null)
        const actionsContainerRef = useRef<HTMLDivElement>(null)

        const [overflowMaxCount, setOverflowMaxCount] = useState(0)
        const effectiveOverflowMaxCount = autoOverflowMaxCount
            ? overflowMaxCount
            : providedOverflowMaxCount

        const calculateOverflowMaxTabsLength = useCallback(
            (listWrapperWidth: number, actionsContainerWidth: number) => {
                const maxAllowedTabsWidth = listWrapperWidth - actionsContainerWidth

                let totalTabsWidth = 0
                let overflowMaxTabsLength = 0
                for (const tabElement of allTabs) {
                    const tab = tabElement.props

                    let tabDesiredWidth =
                        typeof tab.label === 'string' ? tab.label.length * TAB_CHAR_WIDTH : 0
                    if (!!tab.startIcon) {
                        // The gap between the icon and the text is 6px.
                        tabDesiredWidth += TAB_CONTENT_GAP + TAB_ICON_WIDTH
                    }

                    let tabMinWidth = tab.overflowMinWidth || TAB_MIN_WIDTH_NO_ICON
                    if (!!tab.startIcon) {
                        tabMinWidth = TAB_MIN_WIDTH_ICON
                    }

                    let tabMaxWidth = tab.overflowMaxWidth || TAB_MAX_WIDTH_NO_ICON
                    if (!!tab.startIcon) {
                        tabMaxWidth = TAB_MAX_WIDTH_ICON
                    }

                    let tabWidth = Math.min(Math.max(tabDesiredWidth, tabMinWidth), tabMaxWidth)

                    let tempTotalTabsWidth = totalTabsWidth + tabWidth
                    if (tempTotalTabsWidth > maxAllowedTabsWidth) {
                        // The tabs would overflow the parent in this case.
                        // Let's try to reduce the width of this tab.
                        const diffToCorrectWidth = tempTotalTabsWidth - maxAllowedTabsWidth
                        tabWidth -= diffToCorrectWidth

                        if (tabWidth < tabMinWidth) break

                        tempTotalTabsWidth -= diffToCorrectWidth
                    }

                    totalTabsWidth = tempTotalTabsWidth
                    overflowMaxTabsLength++
                }

                setOverflowMaxCount(overflowMaxTabsLength)
            },
            [allTabs]
        )

        useLayoutEffect(() => {
            if (!autoOverflowMaxCount) return

            const listWrapper = listWrapperRef.current
            if (!listWrapper) return

            const actionsContainer = actionsContainerRef.current

            const listWrapperWidth = listWrapper.clientWidth
            const actionsContainerWidth = actionsContainer?.clientWidth || 0
            calculateOverflowMaxTabsLength(listWrapperWidth, actionsContainerWidth)

            const resizeObserver = new ResizeObserver((entries) => {
                let listWrapperEntry: ResizeObserverEntry | undefined
                let actionsContainerEntry: ResizeObserverEntry | undefined
                for (const entry of entries) {
                    if (entry.target === listWrapper) listWrapperEntry = entry
                    if (entry.target === actionsContainer) actionsContainerEntry = entry
                }

                const listWrapperWidth = listWrapperEntry?.contentRect.width || 0
                const actionsContainerWidth = actionsContainerEntry?.contentRect.width || 0
                calculateOverflowMaxTabsLength(listWrapperWidth, actionsContainerWidth)
            })
            resizeObserver.observe(listWrapper)
            if (actionsContainer) {
                resizeObserver.observe(actionsContainer)
            }

            return () => {
                resizeObserver.disconnect()
            }
        }, [autoOverflowMaxCount, calculateOverflowMaxTabsLength])

        const { type, value } = useTabsContext()

        const visibleTabs = useMemo(() => {
            if (overflow) {
                // Make sure selected tab is always visible.
                const selectedIdx = allTabs.findIndex((tab) => tab.props.value === value)
                if (selectedIdx < effectiveOverflowMaxCount) {
                    return allTabs.slice(0, effectiveOverflowMaxCount)
                }

                const selectedItem = allTabs[selectedIdx]
                const orderedTabs = [...allTabs]
                orderedTabs.splice(selectedIdx, 1)
                orderedTabs.splice(effectiveOverflowMaxCount - 1, 0, selectedItem)

                return orderedTabs.slice(0, effectiveOverflowMaxCount)
            }

            return allTabs
        }, [overflow, allTabs, effectiveOverflowMaxCount, value])

        const overflowingTabs = useMemo(() => {
            // Make sure selected tab is always visible.
            const visibleTabValues = new Set(visibleTabs.map((tab) => tab.props.value))

            return allTabs.filter((tab) => !visibleTabValues.has(tab.props.value))
        }, [allTabs, visibleTabs])

        const isOverflowing = allTabs.length > visibleTabs.length

        const [standardProps, { className, ...rootProps }] = extractStandardProps(props)

        const hasAction = isOverflowing || !!endAction

        return (
            <Parts.ListWrapper ref={listWrapperRef} {...standardProps} className={className}>
                <Parts.List
                    {...rootProps}
                    className={type === 'card' ? TabsListCard : TabsListUnderlined}
                    ref={ref}
                    hasAction={hasAction}
                >
                    {visibleTabs}
                </Parts.List>
                {hasAction && (
                    <Parts.ActionsContainer ref={actionsContainerRef}>
                        {isOverflowing && (
                            <>
                                <div role="presentation" className={ActionsDividerStyle} />
                                <ExtraTabsDropdown
                                    renderDropdownItem={renderOverflowDropdownItem}
                                    overflowDropdownHead={overflowDropdownHead}
                                >
                                    {overflowingTabs}
                                </ExtraTabsDropdown>
                            </>
                        )}
                        {endAction && (
                            <>
                                <div
                                    role="presentation"
                                    className={classNames(
                                        ActionsDividerStyle,
                                        ActionsContainerButtonStyle
                                    )}
                                />
                                {endAction}
                            </>
                        )}
                    </Parts.ActionsContainer>
                )}
            </Parts.ListWrapper>
        )
    }
)

type ExtraTabsDropdownProps = {
    children: React.ReactElement[]
    renderDropdownItem?: (
        idx: number,
        child: React.ReactElement,
        props: React.ComponentPropsWithoutRef<typeof DropdownItem>
    ) => React.ReactNode
    overflowDropdownHead?: React.ComponentPropsWithoutRef<typeof DropdownContent>['head']
}

const ExtraTabsDropdown: React.FC<ExtraTabsDropdownProps> = ({
    children,
    renderDropdownItem,
    overflowDropdownHead,
}) => {
    return (
        <Dropdown>
            <DropdownButton
                variant="ghost"
                size="s"
                endIcon={{
                    name: 'ChevronDown',
                }}
            >
                More
            </DropdownButton>
            <ExtraTabsDropdownContent
                renderDropdownItem={renderDropdownItem}
                overflowDropdownHead={overflowDropdownHead}
            >
                {children}
            </ExtraTabsDropdownContent>
        </Dropdown>
    )
}

const ExtraTabsDropdownContent: React.FC<ExtraTabsDropdownProps> = ({
    children,
    renderDropdownItem,
    overflowDropdownHead,
}) => {
    const { value, setValue } = useTabsContext()

    return (
        <DropdownContent onClick={stopPropagation} head={overflowDropdownHead}>
            {children.map((child, idx) => {
                const isActive = child.props.value === value

                const props = {
                    closeOnSelect: true,
                    disabled: child.props.disabled,
                    label: child.props.label,
                    startIcon: child.props.startIcon,
                    startAvatar: child.props.startAvatar,
                    multiSelect: true,
                    checked: isActive,
                    onCheckedChange: (state: boolean) => {
                        if (state) setValue(child.props.value)
                    },
                }

                return renderDropdownItem ? (
                    renderDropdownItem(idx, child, props)
                ) : (
                    <DropdownItem key={child.key} {...props} />
                )
            })}
        </DropdownContent>
    )
}
